/**
 * SKUs / Catalog API – mirrors src/services/skus.ts and routes/skus.ts
 * in vin-pulse-os-api.
 *
 * For Customer role, the backend silently scopes results to status='active'.
 * The frontend mapper here converts the backend's denormalized SKU shape
 * into the frontend's existing `Product` type (src/types/index.ts) so the
 * existing UI components keep working without changes to their props.
 */

import { apiClient } from './apiClient';
import type { Product } from '../types';

export type SkuStatus = 'active' | 'inactive' | 'discontinued';

export interface Sku {
  id: number;
  sku_code: string;
  product_name: string;
  description: string | null;
  category_id: number | null;
  category_name: string | null;
  unit_cost: number;
  list_price: number | null;
  /**
   * VP-CUST-002: customer-tier-adjusted price, present when the caller is a
   * Customer (backend resolves via the caller's pricing_tier discount_factor).
   * Equal to list_price when there's no tier discount, or absent entirely
   * for non-Customer callers.
   */
  customer_price?: number | null;
  age_restriction: '18+' | '21+' | null;
  status: SkuStatus;
  image_url: string | null;
  total_stock: number;
  margin_percent: number;
  created_at: string;
  updated_at: string;
}

export interface ListSkusFilters {
  page?: number;
  limit?: number;
  search?: string;
  category_id?: number;
  status?: SkuStatus;
  in_stock_only?: boolean;
}

export interface PaginatedSkus {
  data: Sku[];
  pagination: { total: number; page: number; limit: number; pages: number };
}

export async function listSkus(filters: ListSkusFilters = {}): Promise<PaginatedSkus> {
  return apiClient.get<PaginatedSkus>('/skus', filters as Record<string, any>);
}

export async function getSku(id: number | string): Promise<Sku> {
  return apiClient.get<Sku>(`/skus/${id}`);
}

/**
 * Convert a backend Sku into the frontend's `Product` shape.
 * Best-effort mapping: backend has no notion of multiple images, ratings,
 * brand, or tags, so those default to sensible empty values.
 */
export function skuToProduct(sku: Sku): Product {
  // VP-CUST-002: prefer the real per-customer tier price when present; fall
  // back to list_price/unit_cost for non-Customer callers or SKUs with no
  // list price at all. originalPrice is only set (and struck through by the
  // UI) when it actually differs from the effective price.
  const listPrice = sku.list_price ?? sku.unit_cost ?? 0;
  const price = sku.customer_price ?? listPrice;
  return {
    id: sku.id,
    name: sku.product_name,
    slug: String(sku.id),
    price,
    originalPrice: listPrice !== price ? listPrice : undefined,
    description: sku.description ?? '',
    shortDescription: sku.description ?? '',
    images: sku.image_url ? [sku.image_url] : [],
    category: sku.category_name ?? '',
    brand: '',
    rating: 0,
    reviewCount: 0,
    inStock: sku.total_stock > 0,
    stockCount: sku.total_stock,
    tags: [],
    features: [],
    sku: sku.sku_code,
  };
}
